import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useEvent,
    useListen,
    useProp,
} from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { useStore } from '@bpd-library/utilities';
import {
    KRPANO_FOV_INCREMENT,
    KRPANO_FOV_MAX,
    KRPANO_FOV_MIN,
    KRPANO_SWF,
    krpanoEmittedEvents,
    krpanoInternalEvents,
} from '@molecules/krpano/constants';
import { KrpanoState, krpanoStore } from '@source/store/krpano';

import { useKrpanoJs } from './utilities/krpano-js';

type KrpanoJSInterface = HTMLElement & {
    get: (key: string) => any;
    set: (key: string, value: any) => void;
    call: (krpanoAction: string) => void;
    spheretoscreen: (h: number, v: number) => { x: number; y: number; z: number };
    screentosphere: (x: number, y: number) => { x: number; y: number; z: number };
};

export interface KrpanoElement extends Component {
    zoomIn: () => void;
    zoomOut: () => void;
    goBack: () => void;
    destroy: () => void;
}

export const Krpano: FC<KrpanoElement> = () => {
    const [id] = useProp<string>('krpanoId', 'krpanoSWFObject');
    const [storeId] = useProp<string>('storeId');

    const [store, subscribe] = useStore<KrpanoState>(krpanoStore);
    let krpano: KrpanoJSInterface;

    useBindMethod('zoomIn', zoomIn);
    useBindMethod('zoomOut', zoomOut);
    useBindMethod('goBack', goBack);
    useBindMethod('destroy', destroy);

    let goBackDisabled = true;
    let zoomInDisabled = false;
    let zoomOutDisabled = false;
    const goBackToggleEvent = useEvent({ eventName: krpanoEmittedEvents.TOGGLE_GO_BACK });
    const zoomInToggleEvent = useEvent({ eventName: krpanoEmittedEvents.TOGGLE_ZOOM_IN });
    const zoomOutToggleEvent = useEvent({ eventName: krpanoEmittedEvents.TOGGLE_ZOOM_OUT });

    useListen(document, krpanoInternalEvents.SCENE_CHANGE, (e) => {
        const initialSceneName = store[storeId]?.initialSceneId;
        if (!initialSceneName) return;
        else if (initialSceneName !== e.detail.sceneName) {
            goBackToggleEvent.emit({ disabled: false });
            goBackDisabled = false;
        }
    });

    function onKrpanoReady(krpanoInterface: KrpanoJSInterface) {
        krpano = krpanoInterface;
    }

    async function updatePano(selectedPano: string) {
        const { removepano, embedpano } = await useKrpanoJs();

        removepano(id);
        embedpano({
            id,
            swf: KRPANO_SWF,
            xml: selectedPano,
            target: `${id}-container`,
            html5: 'prefer',
            mwheel: false,
            initvars: { design: 'flat' },
            passQueryParameters: true,
            onready: onKrpanoReady,
        });
    }

    onDidLoad(() => {
        const { pano } = krpanoStore.getState()[storeId] || {};
        if (pano) updatePano(pano);
    });

    subscribe(
        ({ [storeId]: state }) => {
            state.pano && updatePano(state.pano);
        },
        [storeId],
    );

    function zoomIn() {
        if (!zoomInDisabled) {
            const fov = Number(krpano.get('view.fov'));
            const newFov = fov - KRPANO_FOV_INCREMENT;
            krpano.call(`zoomto(${newFov})`);

            if (newFov <= KRPANO_FOV_MIN) {
                zoomInToggleEvent.emit({ disabled: true });
                zoomInDisabled = true;
            }

            if (zoomOutDisabled) {
                zoomOutToggleEvent.emit({ disabled: false });
                zoomOutDisabled = false;
            }
        }
    }

    function zoomOut() {
        if (!zoomOutDisabled) {
            const fov = Number(krpano.get('view.fov'));
            const newFov = fov + KRPANO_FOV_INCREMENT;
            krpano.call(`zoomto(${newFov})`);

            if (newFov >= KRPANO_FOV_MAX) {
                zoomOutToggleEvent.emit({ disabled: true });
                zoomOutDisabled = true;
            }

            if (zoomInDisabled) {
                zoomInToggleEvent.emit({ disabled: false });
                zoomInDisabled = false;
            }
        }
    }

    function goBack() {
        const initialSceneName = store[storeId].initialSceneId;

        if (!goBackDisabled) {
            krpano.call(`
                if(startScene === null, set(startScene,'${initialSceneName}'); );
                loadScene(get(startScene), null, null, ZOOMBLEND(1.0, 1.0, easeInOutQuad));
            `);
            goBackToggleEvent.emit({ disabled: true });
            goBackDisabled = true;
        }
    }

    async function destroy() {
        const { removepano } = await useKrpanoJs();
        removepano(id);
    }

    return <div class="c-krpano" id={`${id}-container`}></div>;
};

Krpano.props = {
    krpanoId: {
        type: String,
        reflectToAttr: true,
    },
    storeId: {
        type: String,
        required: true,
        reflectToAttr: true,
    },
    viewPath: {
        type: String,
        reflectToAttr: true,
    },
};

defineElement('bpd-krpano', Krpano);
